// Stable className strings backed by globally-injected CSS. Preserves the
// `useStyles().<key>` consumer API without @mui/styles.
//
// Note: the prior makeStyles had several dynamic prop-driven values
// (isMobile, zoom, opt, showMore, hideCap, values). The injected CSS expresses
// the breakpoint-based variants via media queries, and the runtime variants
// via additional class strings appended in the returned object. Consumer
// passes opts to useStyles() and we build the appropriate class combos.
import { useTheme } from '@mui/material/styles';

export const COLORS = {
  darkGrey: '#5F5F5F',
  bgPrimary: 'rgba(0,0,0,0.8)',
  bgPrimaryActive: 'rgba(0,0,0,0.9)',
  bgSecondary: '#5F5F5F',
  accentPrimary: 'red',
  accentPrimaryActive: 'white',
  iconsPrimary: 'black',
  iconsSecondary: 'white',
  header: 'rgba(0,0,0,0.6)',
  footer: 'rgba(0,0,0,0.6)',
  black: '#000000',
  white: '#ffffff',
  disabledText: 'rgba(255,255,255,0.5)',
  loader: 'rgba(0,0,0,0.4)',
  transparent: 'transparent',
  editBar: 'rgba(0,0,0,0.3)',
};

const PREFIX = 'globalMedia';

let injected = false;
const injectStyles = (theme) => {
  if (injected || typeof document === 'undefined') return;
  injected = true;

  const css = `
    /* Filerobot/SfX/FIE third-party overrides (formerly @global) */
    body.Menu-open { overflow: hidden !important; }
    #SfxPopper { position: fixed; z-index: 1300; }
    #SfxPopper .SfxMenu-root, #SfxPopper .SfxMenu-root > div { background-color: ${COLORS.bgPrimary}; }
    #SfxPopper .SfxMenuItem-root { background-color: transparent; }
    #SfxPopper .SfxMenuItem-root:hover { background-color: ${COLORS.darkGrey}; }
    .FIE_editor-content .SfxSelect-root, .FIE_editor-content .SfxInput-fieldWrapper input { color: ${COLORS.black}; }
    .FIE_editor-content .FIE_tool-options-wrapper [title^="Shadow"] { display: none; }
    .FIE_canvas-container { padding: 0px !important; }
    .FIE_canvas-node { background-color: ${COLORS.transparent} !important; }
    .FIE_warmth-tool-button { display: none !important; }
    .FIE_topbar-history-buttons .FIE_topbar-reset-button { display: none; }
    .FIE_tools-item-wrapper label { color: ${COLORS.white} !important; }
    .FIE_image-tool-add-option-button { border-color: ${COLORS.black} !important; }
    .FIE_image-tool-add-option-button .SfxButton-Label { color: ${COLORS.black} !important; }
    .FIE_image-tool-add-option-button:hover { background-color: ${COLORS.black} !important; }
    .FIE_image-tool-add-option-button:hover .SfxButton-Label { color: ${COLORS.white} !important; }
    .FIE_topbar { flex-direction: row-reverse; padding: 8px !important; }
    .FIE_topbar-save-wrapper .FIE_topbar-save-button { background-color: ${COLORS.iconsPrimary}; }
    .FIE_topbar-save-wrapper .FIE_topbar-save-button:hover { background-color: ${COLORS.white}; }
    .FIE_topbar-save-wrapper .FIE_topbar-save-button:hover span { color: ${COLORS.black}; }

    /* mediaDisplay */
    .${PREFIX}-mediaDisplay.printView .caption p { font-size: 18px !important; font-weight: bold; }
    .${PREFIX}-mediaDisplay .caption { color: ${theme.palette.grey['500']}; }
    .${PREFIX}-mediaDisplay.canSelect {
      position: relative;
      border-radius: 4px;
      overflow: hidden;
    }
    .${PREFIX}-mediaDisplay.canSelect::before {
      pointer-events: none;
      content: "";
      position: absolute;
      z-index: 10;
      top: 0;
      left: 0;
      width: 100%;
      height: 0;
      transition: height 0.25s ease-out;
      background: linear-gradient(to bottom, #000000aa 0%, transparent 100%);
    }
    .${PREFIX}-mediaDisplay.canSelect:hover::before { height: 40%; }
    .${PREFIX}-mediaDisplay.canSelect:hover button { opacity: 1; }
    .${PREFIX}-mediaDisplay.canSelect.selected {
      background-color: ${theme.palette.secondary.main}33;
    }
    .${PREFIX}-mediaDisplay.canSelect.selected .smallMediaContainer {
      margin: ${theme.spacing(1.5)};
      width: calc(100% - ${theme.spacing(3)}) !important;
      height: calc(100% - ${theme.spacing(3)}) !important;
    }
    .${PREFIX}-mediaDisplay .smallMediaContainer { position: relative; width: 100%; height: 100%; }
    .${PREFIX}-mediaDisplay .smallMediaContainer .playIcon {
      position: absolute;
      top: calc(50% - ${theme.spacing(2)});
      left: calc(50% - ${theme.spacing(2)});
      color: ${COLORS.white};
      font-size: ${theme.spacing(4)};
    }

    /* editMediaContainer */
    .${PREFIX}-editMediaContainer .loadingwindow {
      position: absolute;
      left: 0; right: 0; bottom: 0; top: 0;
      background-color: ${COLORS.loader};
    }
    .${PREFIX}-editMediaContainer .MuiToggleButtonGroup-root button { background-color: ${COLORS.bgSecondary}; }
    .${PREFIX}-editMediaContainer .MuiToggleButtonGroup-root button.Mui-selected { background-color: ${COLORS.white}; }
    .${PREFIX}-editMediaContainer .MuiToggleButtonGroup-root button.Mui-selected .material-icons { color: ${COLORS.black}; }
    .${PREFIX}-editMediaContainer .material-icons { color: white; }
    .${PREFIX}-editMediaContainer .MuiPaper-root { background-color: ${COLORS.bgSecondary}; color: white; }
    .${PREFIX}-editMediaContainer .MuiDialogHeader-root {
      width: 100%;
      height: ${theme.spacing(8)};
      background-color: ${COLORS.header};
      display: flex;
      flex-direction: row;
      align-items: center;
    }
    .${PREFIX}-editMediaContainer .MuiDialogHeader-root .toolbar {
      margin-left: auto;
      flex-direction: row;
      display: flex;
      align-items: center;
    }
    .${PREFIX}-editMediaContainer .MuiDialogHeader-root .toolbar .tab {
      height: ${theme.spacing(8)};
      padding: 0 16px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }
    .${PREFIX}-editMediaContainer .MuiDialogHeader-root .toolbar .tab.selected { background-color: ${COLORS.bgSecondary}; }
    .${PREFIX}-editMediaContainer .MuiDialogContent-root { padding: 0; position: relative; }
    .${PREFIX}-editMediaContainer .MuiDialogContent-root .ekTTdm { color: white; }
    .${PREFIX}-editMediaContainer .MuiDialogContent-root .fOCVxk { background-color: ${COLORS.bgSecondary}; }
    .${PREFIX}-editMediaContainer .MuiDialogContent-root .FIE_tab:hover svg, .${PREFIX}-editMediaContainer .MuiDialogContent-root .FIE_tab:hover label { color: ${COLORS.iconsSecondary}; }
    .${PREFIX}-editMediaContainer .MuiDialogContent-root .FIE_carousel-item > div { border-radius: 4px; }
    .${PREFIX}-editMediaContainer .MuiDialogContent-root .FIE_carousel-item > div:hover svg { color: white; }

    .${PREFIX}-editMediaContainer .MuiDialogContent-root .imagecontainer {
      position: relative;
      width: 80%;
      height: calc(100% - 32px);
      padding: 16px;
      overflow: hidden;
    }
    .${PREFIX}-editMediaContainer .MuiDialogContent-root .imagecontainer img { object-fit: contain !important; cursor: default; }
    .${PREFIX}-editMediaContainer .MuiDialogContent-root .imagecontainer.noTab { width: 100%; }
    .${PREFIX}-editMediaContainer-mobile .MuiDialogContent-root .imagecontainer { width: 100%; padding: 0; }
    .${PREFIX}-editMediaContainer-zoom .MuiDialogContent-root .imagecontainer img { cursor: move; }

    .${PREFIX}-editMediaContainer .dirButtons .dir {
      position: fixed;
      height: 90px;
      width: 35px;
      top: 47%;
      background-color: ${COLORS.header};
      cursor: pointer;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
    .${PREFIX}-editMediaContainer .dirButtons .dir.prev { left: 0; }
    .${PREFIX}-editMediaContainer .dirButtons .dir.prev .material-icons { margin-left: 5px; }
    .${PREFIX}-editMediaContainer .dirButtons .dir.next { right: 0; }
    .${PREFIX}-editMediaContainer-opt .dirButtons .dir.next { right: calc(20% + 20px); }

    .${PREFIX}-editMediaContainer .sidebar {
      height: calc(100% - 32px);
      background-color: ${COLORS.header};
      width: 20%;
      padding: ${theme.spacing(2)};
      overflow: auto;
      position: relative;
    }
    .${PREFIX}-editMediaContainer-mobile .sidebar {
      background-color: ${COLORS.bgPrimary};
      width: calc(100% - 32px);
      position: absolute;
    }
    .${PREFIX}-editMediaContainer .sidebar .spaceVert { margin: ${theme.spacing(3)} 0; }
    .${PREFIX}-editMediaContainer .sidebar.more button { color: ${COLORS.white}; }
    .${PREFIX}-editMediaContainer .sidebar .cbrow { cursor: pointer; }
    .${PREFIX}-editMediaContainer .MuiCheckbox-root svg { color: ${COLORS.white}; }
    .${PREFIX}-editMediaContainer .MuiSelect-select { background-color: ${COLORS.bgSecondary}; color: ${COLORS.white}; }

    .${PREFIX}-editMediaContainer .MuiDialogActions-root { background-color: ${COLORS.footer}; }
    .${PREFIX}-editMediaContainer .MuiDialogActions-root .link {
      color: ${theme.palette.secondary.main};
      font-weight: bold;
      cursor: pointer;
      padding: ${theme.spacing(1)} 0;
      font-size: 14px;
    }
    .${PREFIX}-editMediaContainer .MuiDialogActions-root .footerContainer {
      flex-direction: row;
      height: ${theme.spacing(8)};
    }
    .${PREFIX}-editMediaContainer-showmore .MuiDialogActions-root .footerContainer,
    .${PREFIX}-editMediaContainer-hidecap .MuiDialogActions-root .footerContainer { flex-direction: column; }
    .${PREFIX}-editMediaContainer-showmore .MuiDialogActions-root .footerContainer { height: auto; }

    .${PREFIX}-editMediaContainer .MuiDialogActions-root .info { width: 25%; display: flex; }
    .${PREFIX}-editMediaContainer-showmore .MuiDialogActions-root .info,
    .${PREFIX}-editMediaContainer-hidecap .MuiDialogActions-root .info { width: 100%; }
    .${PREFIX}-editMediaContainer .MuiDialogActions-root .info svg { font-size: 2rem; }
    @media (max-width: ${theme.breakpoints.values.lg - 0.05}px) {
      .${PREFIX}-editMediaContainer .MuiDialogActions-root .info.right { display: none; }
      .${PREFIX}-editMediaContainer-showmore .MuiDialogActions-root .info.right { display: flex; }
    }

    .${PREFIX}-editMediaContainer .MuiDialogActions-root .infotext {
      margin-left: ${theme.spacing(1)};
      overflow: hidden;
      color: ${COLORS.white};
    }
    .${PREFIX}-editMediaContainer .MuiDialogActions-root .infotext p {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      color: inherit;
    }

    .${PREFIX}-editMediaContainer .MuiDialogActions-root .caption {
      width: auto;
      flex-direction: column;
      display: flex;
      flex-grow: 1;
      align-items: center;
      justify-content: center;
      max-height: 100%;
    }
    .${PREFIX}-editMediaContainer-showmore .MuiDialogActions-root .caption { width: 100%; }
    .${PREFIX}-editMediaContainer .MuiDialogActions-root .caption .descriptionText {
      box-sizing: border-box;
      width: 100%;
      padding: ${theme.spacing(1)} 14px;
    }
    .${PREFIX}-editMediaContainer .MuiDialogActions-root .caption .descriptionText p { margin: 0; }

    .${PREFIX}-editMediaContainer .MuiDialogActions-root .MuiOutlinedInput-root {
      background-color: ${COLORS.bgSecondary};
      color: ${COLORS.white};
    }
    .${PREFIX}-editMediaContainer .MuiDialogActions-root .MuiOutlinedInput-root.Mui-disabled {
      background-color: ${COLORS.transparent};
      -webkit-text-fill-color: ${COLORS.disabledText};
    }
    .${PREFIX}-editMediaContainer .MuiDialogActions-root .MuiOutlinedInput-root.Mui-disabled fieldset { border: none; }
    .${PREFIX}-editMediaContainer .MuiDialogActions-root .MuiOutlinedInput-root .Mui-disabled { -webkit-text-fill-color: ${COLORS.disabledText}; }

    .${PREFIX}-imageCaptions {}
  `;

  const tag = document.createElement('style');
  tag.id = `${PREFIX}-styles`;
  tag.textContent = css;
  document.head.appendChild(tag);
};

const useMediaStyles = (opts = {}) => {
  const theme = useTheme();
  injectStyles(theme);
  const { isMobile, zoom, opt, showMore, hideCap } = opts || {};
  return {
    mediaDisplay: `${PREFIX}-mediaDisplay`,
    editMediaContainer: [
      `${PREFIX}-editMediaContainer`,
      isMobile && `${PREFIX}-editMediaContainer-mobile`,
      zoom > 1 && `${PREFIX}-editMediaContainer-zoom`,
      opt && opt !== 'edit' && `${PREFIX}-editMediaContainer-opt`,
      showMore && `${PREFIX}-editMediaContainer-showmore`,
      hideCap && `${PREFIX}-editMediaContainer-hidecap`,
    ].filter(Boolean).join(' '),
    imageCaptions: `${PREFIX}-imageCaptions`,
  };
};

export default useMediaStyles;
